import {
  ChangeEvent,
  useCallback,
  useLayoutEffect,
  useMemo,
  Ref,
  RefCallback,
  KeyboardEventHandler,
  useState,
} from 'react';

import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  UseComboboxStateChangeTypes,
} from 'downshift';

import {
  type BackwardsCompatibleMutableRefObject,
  mergeRefs,
  useForwardedRef,
} from '@adsk/alloy-react-helpers';
import { useOverlay } from '@adsk/alloy-react-overlay';

import { useGroupFilter } from './index';
import useDropdownId from './useDropdownId';
import useFilteredItems from './useFilteredItems';
import useGroupedItems from './useGroupedItems';
import useKeepMenuOpenOnBlur from './useKeepMenuOpenOnBlur';
import useVirtualProps from './useVirtualProps';
import {
  checkReferenceEquality,
  defaultIsItemDisabled,
  defaultLabelGetter,
  getAriaProps,
  isGroupHeader,
  wrapGetItemProps,
} from '../helpers';
import { getSingleSelectionHandlers } from '../helpers/sharedSingleSelection';
import {
  AutocompleteGetMenuProps,
  GenericItem,
  GetInputProps,
  GetItemProps,
  GetToggleButtonProps,
  GroupHeader,
  InputItemGroup,
  UseAutocompleteProps,
} from '../types';
import useCollapsibleGroups from './useCollapsibleGroups';
import useIMEComposition from './useIMEComposition';

const CLEARS_ON_CLOSE: UseComboboxStateChangeTypes[] = [
  useCombobox.stateChangeTypes.InputBlur,
  useCombobox.stateChangeTypes.ToggleButtonClick,
  useCombobox.stateChangeTypes.FunctionCloseMenu,
];

/**
 * A hook used to manage all logic needed for an Autocomplete (searchable) dropdown.
 */
const useAutocomplete = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement,
>({
  id,
  items,
  groupedItems,
  itemToString = defaultLabelGetter,
  defaultHighlightedIndex = 0,
  target,
  overlayRef: overlayRefProp,
  onInputValueChange: onInputValueChangeProp,
  onIsOpenChange: onIsOpenChangeProp,
  layoutDependency,
  getFilteredItems,
  getMenuProps: getMenuPropsProp,
  initialInputValue,
  stateReducer: stateReducerProp,
  inputRef: inputRefProp,
  isItemDisabled = defaultIsItemDisabled,
  inputValue: inputValueProp,
  enableSearchHighlight,
  ...props
}: UseAutocompleteProps<TItem, TTarget>) => {
  const maybeGroupedItems = items || groupedItems;
  checkReferenceEquality({ ...props, items: maybeGroupedItems });

  const ariaProps = getAriaProps(props);
  const {
    allowCollapsibleGroups,
    showGroupItemCount,
    allowGroupFiltering,
    showEmptyGroups,
  } = props;

  const dropdownId = useDropdownId(id);
  const inputRef = useForwardedRef(inputRefProp || null);
  const targetRef = useForwardedRef(target || null);
  const overlayRef = useForwardedRef(overlayRefProp || null);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string>('group0');
  const [selectedGroupKey, setSelectedGroupKey] = useState<string>();

  const {
    handleCompositionStart,
    getOnCompositionEnd,
    onInputValueChange,
    inputValue,
    setInputValue,
  } = useIMEComposition({
    inputValueProp,
    initialInputValue,
    onInputValueChangeProp,
  });

  const { flatItems, indexGetter, getMenuItems } = useGroupedItems({
    maybeGroupedItems,
    allowCollapsibleGroups,
    expandedGroupKey,
    selectedGroupKey,
  });

  // Only prevent initial filtering if we have both initialInputValue AND initialSelectedItem that match exactly
  const shouldPreventInitialFiltering =
    props.initialSelectedItem &&
    initialInputValue === itemToString(props.initialSelectedItem);

  // Also prevent initial filtering if we only have initialSelectedItem (no explicit initialInputValue)
  // This handles cases where the dropdown opens with a selected item and should show all items initially
  const shouldPreventInitialFilteringWithSelectedItem =
    props.initialSelectedItem && !initialInputValue;

  const [isFilteringActive, setIsFilteringActive] = useState(
    Boolean(initialInputValue) &&
      !shouldPreventInitialFiltering &&
      !shouldPreventInitialFilteringWithSelectedItem,
  );

  const { filteredItems } = useFilteredItems<TItem>({
    items: flatItems,
    getFilteredItems,
    itemToString,
    inputValue: isFilteringActive ? inputValue : '',
  });

  const { filteredGroupItems, showGroupHeaders } = useGroupFilter({
    items: filteredItems,
    maybeGroupedItems,
    selectedGroupKey,
  });

  const visibleItems =
    isFilteringActive || selectedGroupKey ? filteredGroupItems : flatItems;

  const { onKeyDown: controlOnKeyDown, getShouldKeepMenuOpenOnBlur } =
    useKeepMenuOpenOnBlur({ id: dropdownId });

  const handleEnterKeyDown = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      // If no item is being selected (no highlighted item or invalid input), keep the input as-is
      const isNoItemSelected =
        !changes.selectedItem ||
        changes.highlightedIndex === undefined ||
        changes.highlightedIndex < 0;

      if (isNoItemSelected) {
        // If there are no selectable items (e.g., filtered results are all disabled),
        // do nothing: keep menu/input as-is
        const hasAnyVisibleItem = visibleItems.length > 0;
        const hasAnySelectableItem = visibleItems.some((item, idx) => {
          if (isGroupHeader(item)) return false;
          return !isItemDisabled(item as TItem, idx);
        });

        // Only preserve if there are visible items but all are disabled.
        if (hasAnyVisibleItem && !hasAnySelectableItem) {
          return state;
        }

        // If there is a highlighted item in current state but it is disabled, do nothing
        if (
          state.highlightedIndex !== undefined &&
          state.highlightedIndex >= 0
        ) {
          const highlighted = visibleItems[state.highlightedIndex];
          if (
            highlighted &&
            isItemDisabled(highlighted as TItem, state.highlightedIndex)
          ) {
            return state;
          }
        }
        setIsFilteringActive(false);
        return {
          ...changes,
          isOpen: false,
          inputValue: itemToString(state.selectedItem),
        };
      }

      return changes;
    },
    [isItemDisabled, itemToString, visibleItems],
  );

  const handleInputClick = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      if (!state.isOpen) {
        return changes;
      }

      // Only activate filtering if the input has content AND it doesn't match the selected item exactly
      const selectedItemText = state.selectedItem
        ? itemToString(state.selectedItem)
        : '';
      const shouldActivateFiltering =
        inputValue.length > 0 && inputValue !== selectedItemText;
      setIsFilteringActive(shouldActivateFiltering);

      // if the user clicks on the input while the menu is open, then we should keep the menu open with its highlighted index
      return {
        ...changes,
        highlightedIndex: state.highlightedIndex,
        isOpen: true,
      };
    },
    [inputValue, itemToString],
  );

  const handleInputChange = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      if (changes.inputValue === undefined) {
        return changes;
      }

      setIsFilteringActive(true);

      const isSelectAllAndType =
        changes.inputValue.length === 1 && state.inputValue.length > 2;

      if (isSelectAllAndType) {
        // This happens when users select-all and type a new character
        // This causes the minorest of bugs where if you select all but one character and delete it, it will clear the selected value
        return { ...changes, selectedItem: null };
      }

      return changes;
    },
    [],
  );

  const handleOpenMenu = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      const shouldHighlightSelectedItem = changes.isOpen && state.selectedItem;

      if (shouldHighlightSelectedItem && state.selectedItem) {
        // if the user is opening the menu while an item it selected, then let's make sure
        // that the highlighted index is set to the selected index
        return {
          ...changes,
          highlightedIndex: visibleItems.indexOf(state.selectedItem),
          inputValue: itemToString(state.selectedItem),
        };
      }

      return changes;
    },
    [visibleItems, itemToString],
  );

  const handleEscapeKey = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      setIsFilteringActive(false);

      return {
        ...changes,
        isOpen: false,
        inputValue: itemToString(state.selectedItem),
      };
    },
    [itemToString],
  );

  const handleInputBlur = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      const shouldKeepMenuOpen = getShouldKeepMenuOpenOnBlur();

      if (!shouldKeepMenuOpen) {
        setIsFilteringActive(false);
      }

      return {
        ...changes,
        isOpen: shouldKeepMenuOpen,
        // if the user is closing the menu while an item it selected, then let's make sure
        // that input value is reset to the selected item, discarding their incomplete search.
        // If there is no selected item, then we should set the input value to the empty string
        inputValue: itemToString(state.selectedItem),
      };
    },
    [getShouldKeepMenuOpenOnBlur, itemToString],
  );

  const handleItemSelection = useCallback(
    (
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      if (!changes.selectedItem) {
        return changes;
      }

      // Only preserve filtering in specific multi-select scenarios:
      // 1. Custom stateReducer (multiselect)
      // 2. The input is being filtered to a small subset
      // 3. The inputValue is short (<2 chars)
      const isSpecificMultiSelectFiltering =
        stateReducerProp &&
        inputValue &&
        inputValue.length <= 2 && // Short search term
        inputValue !== itemToString(changes.selectedItem);

      if (!isSpecificMultiSelectFiltering) {
        setIsFilteringActive(false);
      }

      return changes;
    },
    [stateReducerProp, inputValue, itemToString],
  );

  const handleMenuClose = useCallback(
    (
      state: UseComboboxState<TItem>,
      changes: Partial<UseComboboxState<TItem>>,
    ): Partial<UseComboboxState<TItem>> => {
      if (changes.isOpen) {
        return changes;
      }

      setIsFilteringActive(false);

      return {
        ...changes,
        // if the user is closing the menu while an item it selected, then let's make sure
        // that input value is reset to the selected item, discarding their incomplete search.
        // If there is no selected item, then we should set the input value to the empty string
        inputValue: itemToString(state.selectedItem),
        // https://github.com/downshift-js/downshift/issues/645#issuecomment-451884359
        // Explicitly reset index so when dowshift opens, it
        // detects the change and scrolls to selected item
        highlightedIndex: -1,
      };
    },
    [itemToString],
  );

  const autocompleteStateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      { changes, type }: UseComboboxStateChangeOptions<TItem>,
    ): Partial<UseComboboxState<TItem>> => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          return handleEnterKeyDown(state, changes);
        case useCombobox.stateChangeTypes.InputClick:
          return handleInputClick(state, changes);
        case useCombobox.stateChangeTypes.InputChange:
          return handleInputChange(state, changes);
        case useCombobox.stateChangeTypes.FunctionOpenMenu:
          return handleOpenMenu(state, changes);
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
          return handleEscapeKey(state, changes);
        case useCombobox.stateChangeTypes.InputBlur:
          return handleInputBlur(state, changes);
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.FunctionSelectItem:
          return handleItemSelection(changes);
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
        case useCombobox.stateChangeTypes.ToggleButtonClick:
          return handleMenuClose(state, changes);
        default:
          return changes;
      }
    },
    [
      handleEnterKeyDown,
      handleInputClick,
      handleInputChange,
      handleOpenMenu,
      handleEscapeKey,
      handleInputBlur,
      handleItemSelection,
      handleMenuClose,
    ],
  );

  const onIsOpenChange = useCallback(
    (args: UseComboboxStateChange<TItem>) => {
      if (!args.isOpen && !args.selectedItem) {
        const closeType = args.type;
        const shouldClearOnClose = CLEARS_ON_CLOSE.includes(closeType);

        if (shouldClearOnClose) {
          setInputValue('');
        }
      }

      onIsOpenChangeProp?.(args);
    },
    [onIsOpenChangeProp, setInputValue],
  );

  const stateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      actionAndChanges: UseComboboxStateChangeOptions<TItem>,
    ) => {
      const updatedState = autocompleteStateReducer(state, actionAndChanges);
      const customUpdatedState = stateReducerProp?.(state, actionAndChanges);
      return {
        ...actionAndChanges.changes,
        ...updatedState,
        ...customUpdatedState,
      };
    },
    [autocompleteStateReducer, stateReducerProp],
  );

  // Wrap isItemDisabled to handle undefined items (can occur when downshift
  // uses stale indices or when items array is empty)
  const wrappedIsItemDisabled = useCallback(
    (item: TItem | undefined, index: number) => {
      if (item === undefined) {
        return true; // Treat undefined items as disabled
      }
      return isItemDisabled(item, index);
    },
    [isItemDisabled],
  );

  const {
    isOpen: downshiftIsMenuOpen,
    getInputProps: rawGetInputProps,
    getItemProps: rawGetItemProps,
    getToggleButtonProps,
    selectItem,
    getMenuProps: rawGetMenuProps,
    openMenu,
    highlightedIndex,
    selectedItem,
    ...useComboboxReturnValue
  } = useCombobox<TItem>({
    id: dropdownId,
    items: visibleItems,
    itemToString,
    initialInputValue,
    onInputValueChange,
    onIsOpenChange,
    defaultHighlightedIndex,
    stateReducer,
    isItemDisabled: wrappedIsItemDisabled,
    ...props,
  });

  const {
    show: useOverlayIsOpen,
    targetProps,
    overlayProps,
  } = useOverlay({
    placement: 'bottom-start',
    show: downshiftIsMenuOpen,
    onHide: useComboboxReturnValue.closeMenu,
    target: targetRef,
    overlayRef,
    layoutDependency,
    isOverlayConditionallyRendered: false,
    ...props,
  });

  const getMenuProps: AutocompleteGetMenuProps = useCallback(
    (options, otherOptions) => {
      const temp = getMenuPropsProp
        ? rawGetMenuProps(
            getMenuPropsProp({ ...overlayProps, ...options }, otherOptions),
            otherOptions,
          )
        : rawGetMenuProps({ ...overlayProps, ...options }, otherOptions);

      return temp;
    },
    [getMenuPropsProp, overlayProps, rawGetMenuProps],
  ) as AutocompleteGetMenuProps;

  const getInputProps = useCallback<GetInputProps>(
    ({ ref: refProp, onKeyDown: onKeyDownProp, ...options } = {}) => {
      const combinedRef: RefCallback<HTMLInputElement> = (element) => {
        if (!element) {
          return;
        }

        if (!refProp || typeof refProp === 'string') {
          // do nothing, we can't use a string ref
        } else if (typeof refProp === 'function') {
          refProp(element);
        } else if (typeof refProp === 'object') {
          (
            refProp as BackwardsCompatibleMutableRefObject<HTMLInputElement>
          ).current = element;
        }

        inputRef.current = element;
      };

      const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        controlOnKeyDown(e);

        onKeyDownProp?.(e);
      };

      const {
        onChange: rawOnChange,
        ref,
        ...inputProps
      } = rawGetInputProps({
        ...options,
        onKeyDown,
        ref: combinedRef,
      });

      const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        setInputValue(event?.target?.value);
        rawOnChange?.(event);
      };

      return {
        onChange,
        onCompositionStart: handleCompositionStart,
        onCompositionEnd: getOnCompositionEnd({ onChangeProp: rawOnChange }),
        ref: ref as Ref<HTMLInputElement>,
        ...inputProps,
      };
    },
    [
      controlOnKeyDown,
      getOnCompositionEnd,
      handleCompositionStart,
      inputRef,
      rawGetInputProps,
      setInputValue,
    ],
  );

  type MenuItems = typeof groupedItems extends InputItemGroup<TItem>[]
    ? (GroupHeader | TItem)[]
    : TItem[];

  const menuItems = useMemo(() => {
    // This is a required extra step to get proper menu item counts from
    // filteredItems which are all filtered items but not group filtered.
    // The group filter requires these counts to ensure group filter buttons
    // behave correctly
    getMenuItems(filteredItems);
    return getMenuItems(visibleItems, false) as MenuItems;
  }, [getMenuItems, filteredItems, visibleItems]);

  const defaultIndexGetter = useCallback(
    (item: TItem) => menuItems.indexOf(item),
    [menuItems],
  );

  const getItemProps: GetItemProps<TItem> = useCallback(
    (args) =>
      wrapGetItemProps({
        getItemProps: rawGetItemProps,
        highlightedIndex,
        selectedItem,
        indexGetter: indexGetter || defaultIndexGetter,
      })(args),
    [
      defaultIndexGetter,
      highlightedIndex,
      indexGetter,
      rawGetItemProps,
      selectedItem,
    ],
  );

  useLayoutEffect(() => {
    mergeRefs(targetProps.ref, targetRef);
  }, [targetProps, targetRef]);

  const virtualProps = useVirtualProps({
    menuItems,
    isGroupHeader,
    showEmptyGroups,
    showGroupHeaders,
  });

  const singleSelectionHandlers = getSingleSelectionHandlers({
    id: dropdownId,
    selectItem,
    closeMenu: useComboboxReturnValue.closeMenu,
    refToFocusOnClear: inputRef,
    selectedItem,
  });

  const { setExpandedGroup } = useCollapsibleGroups({
    allowCollapsibleGroups,
    flatItems: visibleItems,
    maybeGroupedItems,
    highlightedIndex,
    menuItems,
    expandedGroupKey,
    setExpandedGroupKey,
  });

  return {
    controlProps: {
      ref: targetRef,
      isAutocomplete: true,
      isOpen: useOverlayIsOpen,
      getInputProps,
      getToggleButtonProps: getToggleButtonProps as GetToggleButtonProps,
      openMenu,
      selectedItem,
      itemToString,
      selectItem: selectItem as (item: TItem | null) => void,
      ...(props['data-testid'] ? { 'data-testid': props['data-testid'] } : {}),
      ...ariaProps,
    },
    menuProps: {
      isOpen: useOverlayIsOpen,
      isAutocomplete: true,
      menuItems,
      getItemProps,
      getMenuProps,
      highlightedIndex,
      selectedItem,
      width: targetRef.current?.offsetWidth,
      selectItem,
      isGroupHeader,
      allowCollapsibleGroups,
      allowGroupFiltering,
      showGroupItemCount,
      groupedItems: maybeGroupedItems,
      setExpandedGroupKey: setExpandedGroup,
      expandedGroupKey,
      selectedGroupKey,
      setSelectedGroupKey,
      showGroupHeaders,
      setHighlightedIndex: useComboboxReturnValue.setHighlightedIndex,
      refToFocusOnClick: inputRef,
      showEmptyGroups,
      dropdownId,
      inputValue,
      enableSearchHighlight,
      ...singleSelectionHandlers.menuProps,
    },
    /**
     * Props to be provided to `@tanstack/react-virtual`'s `useVirtual` hook when using virtualization.
     */
    virtualProps,
    selectItem,
    ...useComboboxReturnValue,
  };
};

export default Object.assign(useAutocomplete, {
  stateChangeTypes: useCombobox.stateChangeTypes,
});
